import { useCallback, useEffect, useRef, useState } from 'react'
import type { UserState } from 'utils/hooks/use-userInfo'

export const SUPPORT_CHAT_SCRIPT_ID = 'arvore-crm-webchat'
export const SUPPORT_CHAT_SCRIPT_SRC =
  'https://crm-api.arvore.com.br/webchat/widget.js'
export const SUPPORT_CHAT_WIDGET_KEY =
  '9fce30ff5b7bf8ef4b5c131e17c077ad4d020349194dbd55'
export const SUPPORT_CHAT_POLL_INTERVAL_MS = 250
export const SUPPORT_CHAT_LOAD_TIMEOUT_MS = 15000

const getSupportChat = () => window.ArvoreCRM

const getSchoolName = (user?: UserState) =>
  user?.reader?.entity?.name ??
  user?.reader?.root?.name ??
  user?.teacher?.root?.name

export const initSupportChat = () => {
  if (document.getElementById(SUPPORT_CHAT_SCRIPT_ID)) {
    return
  }

  const script = document.createElement('script')
  script.id = SUPPORT_CHAT_SCRIPT_ID
  script.src = SUPPORT_CHAT_SCRIPT_SRC
  script.async = true
  script.setAttribute('data-key', SUPPORT_CHAT_WIDGET_KEY)
  script.setAttribute('data-hide-launcher', 'true')
  document.body.appendChild(script)
}

export const isSupportChatLoaded = (): boolean =>
  typeof getSupportChat()?.open === 'function'

export const openSupportChat = (user?: UserState): boolean => {
  const supportChat = getSupportChat()

  if (typeof supportChat?.open !== 'function') {
    return false
  }

  supportChat.identify?.({
    name: user?.name,
    email: user?.email,
    school: getSchoolName(user)
  })
  supportChat.open()

  return true
}

export const useSupportChat = (user?: UserState) => {
  const [isPendingOpen, setIsPendingOpen] = useState(false)
  const userRef = useRef(user)
  userRef.current = user

  useEffect(() => {
    if (!isPendingOpen) return

    const startedAt = Date.now()
    const interval = setInterval(() => {
      const hasOpened = openSupportChat(userRef.current)
      const hasTimedOut = Date.now() - startedAt >= SUPPORT_CHAT_LOAD_TIMEOUT_MS

      if (hasOpened || hasTimedOut) {
        setIsPendingOpen(false)
      }
    }, SUPPORT_CHAT_POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [isPendingOpen])

  const handleClick = useCallback(() => {
    if (!openSupportChat(userRef.current)) {
      setIsPendingOpen(true)
    }
  }, [])

  const cancelPendingOpen = useCallback(() => {
    setIsPendingOpen(false)
  }, [])

  return { isPendingOpen, handleClick, cancelPendingOpen }
}
